/* Several scores at once, and the two shapes that need them.
 *
 * Both hooks here are fan-outs, and both are **opt-in with the cost stated** for the same reason
 * `useMovements` is: `/scores/latest` carries a value and its coverage per dataset and nothing else,
 * so a per-dimension figure or a full decomposition is one request per dataset. That is the N+1
 * `/scores/latest` exists to prevent, and the answer is not to fabricate the numbers and not to
 * pretend the capability is impossible — it is to make the request count visible and bounded, and
 * to leave the seam a batch endpoint would slot into.
 *
 * A reader turns these on when they are comparing across the estate. Nothing here runs on mount.
 */

import { useQueries } from '@tanstack/react-query';

import { isScore } from '@/shared/api/generated/scoring';
import type { Score } from '@/shared/api/generated/scoring';
import { getOne } from '@/shared/api/http';

/** Past this many rows the dimension columns are refused rather than requested. A reader comparing
 * forty datasets by eye is doing something real; one asking for five hundred at once is asking for
 * a report, and this screen is not that. */
export const DIMENSION_READ_LIMIT = 40;

/** Points of history the chart will resolve into per-dimension lines when asked. A month of daily
 * runs sits inside it; a year does not, and the control says so rather than issuing 365 requests. */
export const HISTORY_READ_LIMIT = 24;

export interface BatchScores<Key extends string> {
  readonly byKey: ReadonlyMap<Key, Score>;
  readonly loading: boolean;
  readonly resolved: number;
  readonly requested: number;
  readonly failed: boolean;
}

/* **An empty list when a fan-out is off, rather than `enabled: false` on every entry.** A disabled
 * query still builds an observer, so a five-hundred-dataset estate would construct five hundred of
 * them on every render to fetch nothing. Both hooks below take that decision before they map. */
function collect<Key extends string>(
  keys: readonly Key[],
  results: readonly { data?: Score | undefined; isLoading: boolean; isError: boolean }[],
  enabled: boolean,
): BatchScores<Key> {
  const byKey = new Map<Key, Score>();
  let loading = false;
  let failed = false;

  results.forEach((result, index) => {
    const key = keys[index];
    if (key === undefined) return;
    if (result.isLoading) loading = true;
    if (result.isError) failed = true;
    if (result.data !== undefined) byKey.set(key, result.data);
  });

  return {
    byKey,
    loading,
    resolved: byKey.size,
    requested: enabled ? keys.length : 0,
    failed,
  };
}

/**
 * The current score for each of several datasets, keyed by **dataset** id.
 *
 * What the ledger's dimension columns are built from. A 404 is the ordinary answer for a dataset no
 * execution has covered, and it simply leaves that key absent — the row already renders "never
 * scored" from the listing and does not need a second, louder way to say so.
 */
export function useDatasetScores(
  datasetIds: readonly string[],
  enabled: boolean,
): BatchScores<string> {
  const wanted = enabled ? datasetIds : [];
  const results = useQueries({
    queries: wanted.map((id) => ({
      /* The same key `useDatasetScore` uses, so opening the drawer on a row whose dimensions are
         already on screen is a cache hit rather than a second read of the same bytes. */
      queryKey: ['scoring', 'score', id],
      queryFn: () =>
        getOne(`/v1/scoring/datasets/${encodeURIComponent(id)}/score`, isScore, 'score'),
      staleTime: 60_000,
      retry: false,
    })),
  });
  return collect(wanted, results, enabled);
}

/**
 * Several scores by **score** id — one trend point each.
 *
 * This is what turns a trend into per-dimension history: a `TrendPoint` carries a `score_id`, and
 * the dimensions live on the `Score` behind it. Bounded to one dataset and to `HISTORY_READ_LIMIT`
 * points, because that is the difference between a comparison somebody asked for and a fan-out
 * across an estate.
 */
export function useScoresByIds(
  scoreIds: readonly string[],
  enabled: boolean,
): BatchScores<string> {
  const wanted = enabled ? scoreIds : [];
  const results = useQueries({
    queries: wanted.map((id) => ({
      queryKey: ['scoring', 'score', 'by-id', id],
      queryFn: () =>
        getOne(`/v1/scoring/scores/${encodeURIComponent(id)}`, isScore, 'score'),
      /* A stored score is immutable — it records the run that produced it — so this can never go
         stale and a refetch could only ever return the identical payload. */
      staleTime: Infinity,
      retry: false,
    })),
  });
  return collect(wanted, results, enabled);
}
